import { useEffect, useMemo, useRef, useState } from 'react';
import throttle from 'lodash-es/throttle';

import { trackEvent } from '~/features/analytics/track-event';

export type ScrollDepthEventProps = {
  threshold: number[];
  analyticsProperties?: Record<string, any>;
  isEnabled?: boolean;
};

/**
 * This hook is used to track the scroll depth of a page.
 * It will trigger the analytics event when the scroll depth reaches the threshold.
 * @param threshold - The threshold to track.
 * @param analyticsProperties - The analytics event properties to track.
 * @param isEnabled - Boolean indicating if scroll depth tracking is enabled.
 * @returns void
 */
export function useScrollDepthEvent({ threshold, analyticsProperties, isEnabled = true }: ScrollDepthEventProps) {
  const [isExhaustive, setIsExhaustive] = useState(false);
  const ref = useRef<Record<number, boolean>>({});
  const sortedThreshold = useMemo(() => {
    return threshold.sort((a, b) => a - b);
  }, [threshold]);

  useEffect(() => {
    const handleScroll = throttle(() => {
      const memory = ref.current;
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      let scrollPercentage: number;
      const denominator = documentHeight - windowHeight;
      if (denominator > 0) {
        scrollPercentage = (scrollPosition / denominator) * 100;
      } else {
        // If the page fits in the viewport, treat as 100% scrolled if at bottom, else 0%
        scrollPercentage = window.scrollY + windowHeight >= documentHeight ? 100 : 0;
      }

      sortedThreshold.forEach((threshold) => {
        const hasTriggeredOnce = memory[threshold];
        if (scrollPercentage >= threshold && !hasTriggeredOnce) {
          trackEvent({
            name: 'Scroll depth trigger',
            type: 'scroll',
            label: `perc_${threshold}`,
            additionalAttributes: analyticsProperties,
          });
          memory[threshold] = true;
        }
      });

      if (sortedThreshold.every((threshold) => memory[threshold])) {
        setIsExhaustive(true);
      }
    }, 100);

    if (!isExhaustive && isEnabled) {
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isExhaustive, sortedThreshold, analyticsProperties, isEnabled]);
}
